const EXPIRATION_FIELDS = ['expiresIn', 'expires_at', 'expiresAt'];

export const getToken = (user) => user?.accessToken || user?.token || user?.jwt || null;

const decodeBase64 = (value) => {
    if (typeof atob === 'function') return atob(value);
    // eslint-disable-next-line no-undef
    if (typeof Buffer !== 'undefined') return Buffer.from(value, 'base64').toString('binary');
    return null;
};

const decodeJwtPayload = (token) => {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;

        const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = normalizedPayload.padEnd(
            normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
            '='
        );
        const decodedPayload = decodeBase64(paddedPayload);
        if (!decodedPayload) return null;

        return JSON.parse(decodedPayload);
    } catch {
        return null;
    }
};

const getJwtExpirationTime = (token) => {
    const exp = decodeJwtPayload(token)?.exp;
    return Number.isFinite(exp) ? exp * 1000 : null;
};

export const getExpirationTime = (user) => {
    const expirationField = EXPIRATION_FIELDS.find((field) => user?.[field]);
    const expiresIn = expirationField ? user[expirationField] : null;

    if (expiresIn) {
        const numericExpiration = Number(expiresIn);
        if (!Number.isNaN(numericExpiration)) {
            return numericExpiration < 10000000000 ? Date.now() + numericExpiration * 1000 : numericExpiration;
        }

        const parsed = Date.parse(expiresIn);
        if (!Number.isNaN(parsed)) return parsed;
    }

    return getJwtExpirationTime(getToken(user));
};

export const normalizeSession = (user) => {
    if (!user || typeof user !== 'object') return null;

    const token = getToken(user);
    const expirationTime = getExpirationTime(user);

    return {
        ...user,
        accessToken: token,
        expiresIn: expirationTime,
    };
};

export const isValidSession = (user) => {
    if (!user || typeof user !== 'object') return false;

    const token = getToken(user);
    if (!token) return false;

    const expirationTime = getExpirationTime(user);
    return Boolean(expirationTime && expirationTime > Date.now());
};

export const isExpiredSession = (user) => !isValidSession(user);

/** Milissegundos até a sessão expirar. Zero quando já expirou ou não há sessão. */
export const getMsUntilExpiration = (user) => {
    const expirationTime = user ? getExpirationTime(user) : null;
    return expirationTime ? Math.max(0, expirationTime - Date.now()) : 0;
};

/**
 * Sessão com o token renovado. Troca o token e a expiração, e só isso: e-mail, usuário e tenant
 * guardado continuam valendo.
 *
 * Uma resposta sem token, sem expiração ou já vencida NÃO é aplicada. Devolve nulo, e a sessão
 * atual, que ainda vale, fica. Trocar uma sessão que funciona por uma pela metade derrubaria o
 * usuário justo na chamada feita para mantê-lo dentro.
 */
export const applyRenewedToken = (user, accessToken, expiresIn) => {
    if (!user || !accessToken || !expiresIn) return null;
    const renewed = normalizeSession({...user, accessToken, expiresIn});
    return isValidSession(renewed) ? renewed : null;
};

/** Tipo de acesso do token (`ADMIN`, `SUPPORT` ou `USER`), ou nulo. */
export const getUserTypeFromToken = (token) => (token ? decodeJwtPayload(token)?.userType : null) || null;

/**
 * Tenant da assinatura de uma conta que não é interna, tirado do token (BBF-110).
 *
 * USER só passa no identity com o tenant de uma licença dele, e o login não o devolve. O token
 * traz os tenants das licenças do sistema do login. No backoffice, que é sistema interno, só
 * existe uma (`busifarm-backoffice`). Com mais de um, não há como escolher aqui, e nada é mandado.
 *
 * ADMIN e SUPPORT seguem sem tenant. Desde a BBF-181 o token não traz mais `identityDomain`:
 * quem decide é o `userType`.
 */
export const getTenantIdFromToken = (token) => {
    const payload = token ? decodeJwtPayload(token) : null;
    if (!payload) return null;

    if (['ADMIN', 'SUPPORT'].includes(payload.userType)) return null;

    const tenants = Array.isArray(payload.allowedTenantIds) ? payload.allowedTenantIds : [];
    return tenants.length === 1 ? tenants[0] : null;
};
